/**
 * Content / policy pages: sticky "On this page" nav with active-section tracking.
 * On Shopify policy pages, builds the TOC from H2s inside [data-pj-policy-html].
 */
class PjContentPage extends HTMLElement {
  connectedCallback() {
    this.#buildPolicyToc();

    this.topicButtons = [...this.querySelectorAll('[data-pj-content-topic]')];
    this.groups = [...this.querySelectorAll('[data-pj-content-group]')];
    this.#activeId = this.topicButtons[0]?.dataset.pjContentTopic || '';
    this.#scrolling = false;

    this.topicButtons.forEach((button) => {
      button.addEventListener('click', (event) => this.#onTopicClick(event, button));
    });

    if (!this.groups.length) return;

    this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      root: null,
      rootMargin: '-30% 0px -55% 0px',
      threshold: 0,
    });
    this.groups.forEach((group) => this.#observer.observe(group));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {string} */
  #activeId = '';

  /** @type {boolean} */
  #scrolling = false;

  /**
   * Build sidebar links from Admin policy H2 headings.
   */
  #buildPolicyToc() {
    const htmlRoot = this.querySelector('[data-pj-policy-html]');
    const topicsList = this.querySelector('[data-pj-content-topics-auto]');
    if (!(htmlRoot instanceof HTMLElement) || !(topicsList instanceof HTMLElement)) return;

    const headings = [...htmlRoot.querySelectorAll('h2')];
    if (!headings.length) return;

    topicsList.replaceChildren();

    headings.forEach((heading, index) => {
      if (!(heading instanceof HTMLElement)) return;

      let id = heading.id;
      if (!id) {
        const base =
          heading.textContent
            ?.trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '') || `section-${index + 1}`;
        id = `policy-${base}`;
        heading.id = id;
      }

      heading.setAttribute('data-pj-content-group', '');

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = `pj-content__topic${index === 0 ? ' is-active' : ''}`;
      link.href = `#${id}`;
      link.dataset.pjContentTopic = id;
      link.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      link.textContent = heading.textContent?.trim() || `Section ${index + 1}`;
      item.appendChild(link);
      topicsList.appendChild(item);
    });
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLElement} button
   */
  #onTopicClick(event, button) {
    event.preventDefault();
    const id = button.dataset.pjContentTopic;
    const target = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!(target instanceof HTMLElement)) return;

    this.#setActive(id);
    this.#scrolling = true;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.setTimeout(() => {
      this.#scrolling = false;
    }, 800);
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect(entries) {
    if (this.#scrolling) return;

    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

    const id = visible[0]?.target?.id;
    if (id) this.#setActive(id);
  }

  /**
   * @param {string} id
   */
  #setActive(id) {
    if (!id || id === this.#activeId) return;
    this.#activeId = id;

    this.topicButtons.forEach((button) => {
      const isActive = button.dataset.pjContentTopic === id;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('pj-content-page')) {
  customElements.define('pj-content-page', PjContentPage);
}
